import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { DisciplineRatingsSection } from "@/components/dashboard/DisciplineRatingsSection";
import { EngagementsTimeline } from "@/components/dashboard/EngagementsTimeline";
import { GuidanceOutlook } from "@/components/dashboard/GuidanceOutlook";
import { DisciplineChip } from "@/components/shared/discipline-chip";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { constraintCellBackground, constraintGroupHue } from "@/lib/constraint-colors";
import { jsonFetch } from "@/lib/json-fetch";

interface ExternalNymblPerson {
  id: string;
  nymblPersonId: string;
}

interface ResourceCard {
  id: string;
  name: string;
  email: string;
  initials: string;
  capacity: number;
  points: number;
  active: boolean;
  externalNymblPersonId: string | null;
  externalNymblPerson: ExternalNymblPerson | null;
  competencies: Array<{ id: string; name: string; proficiency: number | null }>;
  constraints: Array<{
    id: string;
    name: string;
    group: string;
    rating: number | null;
    ratedBy: { id: string; name: string } | null;
    ratedAt: string | null;
  }>;
}

interface ConstraintLibraryGroup {
  group: string;
  items: Array<{ id: string; name: string }>;
}

interface DisciplineRef {
  id: string;
  code: string;
  name: string;
  active: boolean;
}

interface DisciplineGrant {
  grantId: string;
  disciplineId: string;
  code: string;
  name: string;
}

interface ExternalNymblPersonRow {
  id: string;
  nymblPersonId: string;
  linkedResourceId: string;
  linkedResourceName: string;
  linkedResourceEmail: string;
  lastSyncedAt: string;
}

function ratingClass(r: number | null) {
  if (r == null) return "bg-muted text-muted-foreground";
  if (r >= 5) return "bg-success text-white";
  if (r >= 4) return "bg-[var(--fit-strong)] text-white";
  if (r >= 3) return "bg-[var(--fit-moderate)] text-white";
  return "bg-[var(--fit-at-risk)] text-white";
}

// Canonical Constraint group order for the rating editor.
const CONSTRAINT_GROUP_ORDER = ["Domain", "Platform", "Offering", "ICP"] as const;

function auditInitials(name: string): string {
  const letters = name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0))
    .join("")
    .toUpperCase();
  return letters.slice(0, 2) || "?";
}

function formatEditDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function ModalShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center bg-black/40 p-12">
      <div className="w-full max-w-[520px] rounded-md border border-border bg-background p-4 shadow-lg">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-display text-[15px] font-bold">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function EditDetailsDialog({
  resource,
  onClose,
  onSaved,
}: {
  resource: ResourceCard;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(resource.name);
  const [capacity, setCapacity] = useState(resource.capacity);
  const [externalId, setExternalId] = useState<string | null>(resource.externalNymblPersonId);
  const [search, setSearch] = useState("");

  const { data: ext } = useQuery<{ persons: ExternalNymblPersonRow[] }>({
    queryKey: ["external-nymbl-persons", search],
    queryFn: () =>
      fetch(`/api/external-nymbl-persons?limit=25&search=${encodeURIComponent(search)}`).then((r) =>
        r.json(),
      ),
  });

  const save = useMutation({
    mutationFn: async () => {
      const patch: {
        name?: string;
        capacity?: number;
        externalNymblPersonId?: string | null;
      } = {};
      if (name.trim() !== resource.name) patch.name = name.trim();
      if (capacity !== resource.capacity) patch.capacity = capacity;
      if (externalId !== resource.externalNymblPersonId) patch.externalNymblPersonId = externalId;
      if (Object.keys(patch).length === 0) return { ok: true };
      const r = await fetch(`/api/dashboard/resources/${resource.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      if (!r.ok) throw new Error((await r.json()).error ?? "Failed");
      return r.json();
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  const currentLink =
    ext?.persons.find((p) => p.id === externalId) ??
    (resource.externalNymblPerson && externalId === resource.externalNymblPersonId
      ? {
          id: resource.externalNymblPerson.id,
          nymblPersonId: resource.externalNymblPerson.nymblPersonId,
          linkedResourceName: resource.name,
          linkedResourceEmail: resource.email,
        }
      : null);

  return (
    <ModalShell title="Edit Resource" onClose={onClose}>
      <label className="mb-3 block">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Name
        </span>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="mt-1 w-full rounded-md border border-border bg-card p-2 text-[12px] outline-none focus:border-foreground/30"
        />
      </label>

      <label className="mb-3 block">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Capacity (points)
        </span>
        <input
          type="number"
          min={0}
          max={40}
          value={capacity}
          onChange={(e) => setCapacity(Number.parseInt(e.target.value, 10) || 0)}
          className="mt-1 w-full rounded-md border border-border bg-card p-2 text-[12px] outline-none focus:border-foreground/30"
        />
        <p className="mt-1 text-[10px] text-muted-foreground">
          Total points this Resource can carry across active Assignments.
        </p>
      </label>

      <div className="mb-3">
        <div className="mb-1 flex items-center justify-between">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Nymbl Platform link
          </span>
          {externalId && (
            <button
              type="button"
              onClick={() => setExternalId(null)}
              className="text-[10px] font-medium text-muted-foreground hover:text-[var(--fit-at-risk)]"
            >
              Unlink
            </button>
          )}
        </div>
        {currentLink ? (
          <div className="mb-2 rounded-md border border-border bg-card px-2 py-1.5 text-[11px]">
            <div className="font-medium">{currentLink.linkedResourceName}</div>
            <div className="text-muted-foreground">
              {currentLink.linkedResourceEmail} · {currentLink.nymblPersonId}
            </div>
          </div>
        ) : (
          <div className="mb-2 rounded-md border border-dashed border-border bg-muted/40 px-2 py-1.5 text-[11px] text-muted-foreground">
            Not linked to a Nymbl Person.
          </div>
        )}
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, email, or Nymbl Person ID…"
          className="w-full rounded-md border border-border bg-card p-2 text-[12px] outline-none focus:border-foreground/30"
        />
        <div className="mt-1 max-h-40 overflow-y-auto rounded-md border border-border bg-card">
          {(ext?.persons ?? []).map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setExternalId(p.id)}
              className={`flex w-full items-center justify-between gap-2 px-2 py-1.5 text-left text-[11px] hover:bg-muted ${
                externalId === p.id ? "bg-muted" : ""
              }`}
            >
              <span className="min-w-0">
                <span className="block truncate font-medium">{p.linkedResourceName}</span>
                <span className="block truncate text-muted-foreground">
                  {p.linkedResourceEmail}
                </span>
              </span>
              <span className="shrink-0 font-mono text-[10px] text-muted-foreground">
                {p.nymblPersonId.slice(0, 8)}…
              </span>
            </button>
          ))}
          {(ext?.persons ?? []).length === 0 && (
            <div className="px-2 py-3 text-center text-[11px] text-muted-foreground">
              No matches.
            </div>
          )}
        </div>
      </div>

      {save.isError && (
        <div className="mb-3 rounded bg-[var(--fit-at-risk-bg)] px-2 py-1.5 text-[11px] text-[var(--fit-at-risk)]">
          {String((save.error as Error).message).slice(0, 200)}
        </div>
      )}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="rounded border border-border px-3 py-1 text-[12px] hover:bg-muted"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={save.isPending || name.trim().length === 0}
          onClick={() => save.mutate()}
          className="rounded bg-foreground px-3 py-1 text-[12px] font-semibold text-background disabled:opacity-40"
        >
          {save.isPending ? "Saving…" : "Save"}
        </button>
      </div>
    </ModalShell>
  );
}

function RatingSelector({
  value,
  onChange,
}: {
  value: number | null;
  onChange: (v: number | null) => void;
}) {
  return (
    <div className="inline-flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(value === n ? null : n)}
          className={`flex h-6 w-6 items-center justify-center rounded text-[10px] font-semibold ${
            value === n
              ? ratingClass(n)
              : "border border-border bg-card text-muted-foreground hover:bg-muted"
          }`}
        >
          {n}
        </button>
      ))}
      <button
        type="button"
        onClick={() => onChange(null)}
        className={`ml-1 rounded px-1.5 py-0.5 text-[10px] font-medium ${
          value === null
            ? "bg-muted text-foreground"
            : "text-muted-foreground hover:text-foreground"
        }`}
        title="Clear"
      >
        Clear
      </button>
    </div>
  );
}

function EditConstraintRatingsDialog({
  resource,
  onClose,
  onSaved,
}: {
  resource: ResourceCard;
  onClose: () => void;
  onSaved: () => void;
}) {
  // Full Constraint library (all active Constraints, grouped) so the RM can rate
  // — and thereby add — any Constraint, not just the ones already on the Resource.
  const { data: lib } = useQuery<{ groups: ConstraintLibraryGroup[] }>({
    queryKey: ["dashboard-constraints"],
    queryFn: () => jsonFetch<{ groups: ConstraintLibraryGroup[] }>("/api/dashboard/constraints"),
  });

  // RM-confirmed ratings already on this Resource, keyed by Constraint id — keeps
  // the rating plus the light audit trail (who confirmed it, and when).
  const existing = useMemo(
    () => new Map(resource.constraints.map((c) => [c.id, c])),
    [resource.constraints],
  );

  // Sparse overrides the RM has touched this session.
  const [draft, setDraft] = useState<Record<string, number | null>>({});
  const valueFor = (id: string): number | null =>
    id in draft ? (draft[id] ?? null) : (existing.get(id)?.rating ?? null);

  const orderedGroups = useMemo(() => {
    const order: Record<string, number> = Object.fromEntries(
      CONSTRAINT_GROUP_ORDER.map((g, i) => [g, i]),
    );
    return [...(lib?.groups ?? [])].sort((a, b) => (order[a.group] ?? 99) - (order[b.group] ?? 99));
  }, [lib]);

  const save = useMutation({
    mutationFn: async () => {
      // Send only Constraints whose rating actually changed. A previously
      // unassigned Constraint has no existing rating, so setting one inserts a
      // row (the endpoint upserts) — that's how a Constraint gets added.
      const ratings = Object.entries(draft)
        .filter(([id, v]) => (existing.get(id)?.rating ?? null) !== (v ?? null))
        .map(([constraintId, v]) => ({ constraintId, rmConfirmedRating: v ?? null }));
      if (ratings.length === 0) return { updated: 0 };
      const r = await fetch(`/api/dashboard/resources/${resource.id}/constraint-ratings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ratings }),
      });
      if (!r.ok) throw new Error((await r.json()).error ?? "Failed");
      return r.json();
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  return (
    <ModalShell title="Edit Constraint Ratings" onClose={onClose}>
      <p className="mb-3 text-[11px] text-muted-foreground">
        RM-confirmed ratings (1–5) feed Fit Score. Rating a Constraint adds it to this Resource; use
        Clear to remove a confirmed value.
      </p>
      <div className="mb-3 max-h-[60vh] space-y-3 overflow-y-auto">
        {orderedGroups.map((grp) => (
          <div key={grp.group}>
            <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              {grp.group}
            </div>
            <div className="space-y-1.5 rounded-md border border-border bg-card p-2">
              {grp.items.map((c) => {
                const audit = existing.get(c.id);
                return (
                  <div key={c.id} className="flex items-center justify-between gap-2 text-[12px]">
                    <span className="truncate">{c.name}</span>
                    <div className="flex shrink-0 items-center gap-2">
                      <RatingSelector
                        value={valueFor(c.id)}
                        onChange={(v) => setDraft((d) => ({ ...d, [c.id]: v }))}
                      />
                      {audit?.ratedBy ? (
                        <span
                          className="w-5 text-right font-mono text-[9px] font-semibold tracking-wide text-muted-foreground"
                          title={`Edited by ${audit.ratedBy.name}${
                            audit.ratedAt ? ` · ${formatEditDate(audit.ratedAt)}` : ""
                          } · ${audit.ratedBy.id}`}
                        >
                          {auditInitials(audit.ratedBy.name)}
                        </span>
                      ) : (
                        <span className="w-5" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        {orderedGroups.length === 0 && (
          <div className="rounded-md border border-dashed border-border bg-card/50 px-3 py-6 text-center text-[11px] text-muted-foreground">
            Loading Constraints…
          </div>
        )}
      </div>
      {save.isError && (
        <div className="mb-3 rounded bg-[var(--fit-at-risk-bg)] px-2 py-1.5 text-[11px] text-[var(--fit-at-risk)]">
          {String((save.error as Error).message).slice(0, 200)}
        </div>
      )}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="rounded border border-border px-3 py-1 text-[12px] hover:bg-muted"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={save.isPending}
          onClick={() => save.mutate()}
          className="rounded bg-foreground px-3 py-1 text-[12px] font-semibold text-background disabled:opacity-40"
        >
          {save.isPending ? "Saving…" : "Save"}
        </button>
      </div>
    </ModalShell>
  );
}

function EditDisciplinesDialog({
  resource,
  onClose,
  onSaved,
}: {
  resource: ResourceCard;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { data: all } = useQuery<DisciplineRef[]>({
    queryKey: ["disciplines"],
    queryFn: () => jsonFetch<DisciplineRef[]>("/api/disciplines"),
  });
  const { data: grants } = useQuery<DisciplineGrant[]>({
    queryKey: ["resource-disciplines", resource.id],
    queryFn: () => jsonFetch<DisciplineGrant[]>(`/api/resources/${resource.id}/disciplines`),
  });

  const grantByDisciplineId = useMemo(
    () => new Map((grants ?? []).map((g) => [g.disciplineId, g])),
    [grants],
  );

  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Single-click grant/revoke with no reason and no keep-one guard.
  const toggle = async (disciplineId: string, currentlyActive: boolean) => {
    setError(null);
    setBusyId(disciplineId);
    try {
      if (currentlyActive) {
        const grant = grantByDisciplineId.get(disciplineId);
        if (!grant) throw new Error("Grant not found");
        const r = await fetch(`/api/resources/${resource.id}/disciplines/${grant.grantId}`, {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: "{}",
        });
        if (!r.ok) throw new Error((await r.json()).error ?? "Failed");
      } else {
        const r = await fetch(`/api/resources/${resource.id}/disciplines`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ disciplineId }),
        });
        if (!r.ok) throw new Error((await r.json()).error ?? "Failed");
      }
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <ModalShell title="Edit Disciplines" onClose={onClose}>
      <p className="mb-3 text-[11px] text-muted-foreground">
        Grant or revoke Disciplines. Each change is audit-logged.
      </p>
      <div className="mb-3 max-h-[60vh] space-y-1.5 overflow-y-auto">
        {(all ?? []).map((d) => {
          const active = grantByDisciplineId.has(d.id);
          return (
            <div
              key={d.id}
              className="flex items-center justify-between gap-2 rounded-md border border-border bg-card px-2 py-1.5 text-[12px]"
            >
              <div className="min-w-0">
                <DisciplineChip code={d.code} />{" "}
                <span className="text-muted-foreground">{d.name}</span>
                {!d.active && (
                  <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[9px] uppercase text-muted-foreground">
                    Inactive
                  </span>
                )}
              </div>
              <button
                type="button"
                disabled={busyId === d.id || (!active && !d.active)}
                onClick={() => toggle(d.id, active)}
                className={`rounded px-2 py-1 text-[11px] font-medium disabled:opacity-40 ${
                  active
                    ? "border border-[var(--fit-at-risk)]/40 text-[var(--fit-at-risk)] hover:bg-[var(--fit-at-risk-bg)]"
                    : "bg-foreground text-background hover:opacity-90"
                }`}
              >
                {busyId === d.id ? "…" : active ? "Revoke" : "Grant"}
              </button>
            </div>
          );
        })}
      </div>
      {error && (
        <div className="mb-3 rounded bg-[var(--fit-at-risk-bg)] px-2 py-1.5 text-[11px] text-[var(--fit-at-risk)]">
          {error}
        </div>
      )}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={onClose}
          className="rounded border border-border px-3 py-1 text-[12px] hover:bg-muted"
        >
          Done
        </button>
      </div>
    </ModalShell>
  );
}

type EditPanel = "details" | "constraints" | "disciplines" | null;

function SectionHeader({ label, onEdit }: { label: string; onEdit: () => void }) {
  return (
    <div className="mb-2 flex items-center justify-between">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <button
        type="button"
        onClick={onEdit}
        className="inline-flex items-center gap-1 rounded border border-border bg-card px-1.5 py-0.5 text-[10px] font-medium text-foreground hover:bg-muted"
      >
        <Pencil className="h-2.5 w-2.5" />
        Edit
      </button>
    </div>
  );
}

export function ResourceDetailDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const qc = useQueryClient();
  const { data } = useQuery<{ resources: ResourceCard[] }>({
    queryKey: ["dashboard-resources"],
    queryFn: () => jsonFetch<{ resources: ResourceCard[] }>("/api/dashboard/resources"),
  });
  const resource = data?.resources.find((r) => r.id === id);

  const [panel, setPanel] = useState<EditPanel>(null);

  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (panel) setPanel(null);
      else onClose();
    };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [onClose, panel]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["dashboard-resources"] });
    qc.invalidateQueries({ queryKey: ["dashboard-solutions"] });
    qc.invalidateQueries({ queryKey: ["dashboard-heatmap"] });
    qc.invalidateQueries({ queryKey: ["resource-disciplines", id] });
    qc.invalidateQueries({ queryKey: ["solution-detail"] });
  };

  const groups = ["Domain", "Platform", "Offering", "ICP"];

  return (
    <>
      <button
        type="button"
        aria-label="Close drawer"
        className="fixed inset-0 z-40 bg-black/40"
        onClick={onClose}
      />
      <aside className="fixed right-0 top-0 z-50 flex h-screen w-full max-w-[640px] flex-col border-l border-border bg-background shadow-[0_0_40px_rgba(0,24,71,0.18)]">
        <header className="flex items-start justify-between border-b border-border px-5 py-4">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-foreground/85 text-[14px] font-semibold text-background">
              {resource?.initials ?? "—"}
            </span>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="font-display text-[16px] font-bold leading-tight">
                  {resource?.name ?? "Loading…"}
                </h2>
                {resource && (
                  <button
                    type="button"
                    onClick={() => setPanel("details")}
                    className="inline-flex items-center gap-1 rounded border border-border bg-card px-1.5 py-0.5 text-[10px] font-medium text-foreground hover:bg-muted"
                    title="Edit Resource details"
                  >
                    <Pencil className="h-2.5 w-2.5" />
                    Edit
                  </button>
                )}
              </div>
              <div className="text-[11px] text-muted-foreground">{resource?.email}</div>
              {resource?.externalNymblPerson && (
                <div className="mt-0.5 text-[10px] text-muted-foreground">
                  Nymbl Person:{" "}
                  <span className="font-mono">{resource.externalNymblPerson.nymblPersonId}</span>
                </div>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        {resource && (
          <Tabs defaultValue="profile" className="flex-1 gap-0 overflow-hidden">
            <TabsList className="mx-5 mt-3 w-fit shrink-0">
              <TabsTrigger value="profile">Profile</TabsTrigger>
              <TabsTrigger value="engagements">Engagements</TabsTrigger>
            </TabsList>

            <TabsContent value="profile" className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
              <section className="mb-5">
                <SectionHeader label="Capacity" onEdit={() => setPanel("details")} />
                <div className="rounded-md border border-border bg-card p-3 text-[12px]">
                  <div className="flex items-center justify-between">
                    <span>Currently allocated</span>
                    <span className="font-display text-[14px] font-bold">
                      {resource.points}/{resource.capacity} pts
                    </span>
                  </div>
                  <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${Math.min(100, (resource.points / Math.max(1, resource.capacity)) * 100)}%`,
                        background:
                          resource.points > resource.capacity
                            ? "var(--fit-at-risk)"
                            : "var(--brand-600)",
                      }}
                    />
                  </div>
                </div>
              </section>

              <GuidanceOutlook resourceId={resource.id} resourceName={resource.name} />

              <section className="mb-5">
                <SectionHeader label="Disciplines" onEdit={() => setPanel("disciplines")} />
                <DisciplinesSummary resourceId={resource.id} />
              </section>

              <section className="mb-5">
                <div
                  className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
                  title="Discipline Rating (1–5) vs Expectation Level — the Fit Score Expectation dimension input."
                >
                  Discipline Ratings
                </div>
                <DisciplineRatingsSection resourceId={resource.id} />
              </section>

              <section className="mb-5">
                <div className="mb-2 flex items-center gap-2">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Competencies
                  </span>
                  <span
                    className="rounded bg-muted px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-muted-foreground"
                    title="Deprecated — Competency proficiency no longer feeds Fit Score (see Discipline Ratings above). Retained read-only for audit."
                  >
                    Legacy · read-only
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {resource.competencies.map((c) => (
                    <div
                      key={c.id}
                      className="flex flex-col items-center gap-2 rounded-md border border-border bg-card p-3 text-center"
                    >
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                        {c.name}
                      </div>
                      <div className="font-display text-[24px] font-bold leading-none">
                        {c.proficiency ?? "—"}
                      </div>
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map((n) => {
                          const filled = (c.proficiency ?? 0) >= n;
                          return (
                            <span
                              key={n}
                              className="h-3 w-3 rounded-[3px]"
                              style={
                                filled
                                  ? { background: "var(--primary)" }
                                  : {
                                      background: "var(--card)",
                                      boxShadow: "inset 0 0 0 1px var(--border)",
                                    }
                              }
                            />
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <SectionHeader label="Constraint Ratings" onEdit={() => setPanel("constraints")} />
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-[9px] uppercase tracking-wider text-muted-foreground">
                  <span>hue = constraint group · intensity = strength</span>
                  <span className="flex items-center gap-1.5">
                    <span>1 · light</span>
                    <span className="flex items-center gap-px">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <span
                          key={n}
                          className="h-2.5 w-2.5 rounded-[2px]"
                          style={{ background: constraintCellBackground("Domain", n) }}
                        />
                      ))}
                    </span>
                    <span>5 · deep</span>
                  </span>
                </div>
                <div className="space-y-2">
                  {groups.map((g) => {
                    const items = resource.constraints.filter((c) => c.group === g);
                    if (items.length === 0) return null;
                    const hue = constraintGroupHue(g);
                    return (
                      <div key={g} className="rounded-md border border-border bg-card p-3">
                        <div
                          className="mb-1.5 text-[10px] font-bold uppercase tracking-wider"
                          style={{ color: hue }}
                        >
                          {g}
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {items.map((c) => {
                            const rated = c.rating != null;
                            const deep = (c.rating ?? 0) >= 3;
                            return (
                              <span
                                key={c.id}
                                className="inline-flex items-center gap-1.5 rounded px-1.5 py-0.5 text-[11px] font-semibold"
                                title={`${c.name}: ${c.rating ?? "unrated"}`}
                                style={
                                  rated
                                    ? {
                                        background: constraintCellBackground(g, c.rating),
                                        color: deep
                                          ? "var(--primary-foreground)"
                                          : "var(--foreground)",
                                      }
                                    : {
                                        boxShadow: `inset 0 0 0 1px ${hue}40`,
                                        color: "var(--muted-foreground)",
                                      }
                                }
                              >
                                <span>{c.name}</span>
                                <span className="opacity-90 tabular-nums">{c.rating ?? "—"}</span>
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            </TabsContent>

            <TabsContent value="engagements" className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
              <EngagementsTimeline resourceId={resource.id} />
            </TabsContent>
          </Tabs>
        )}
      </aside>

      {resource && panel === "details" && (
        <EditDetailsDialog
          resource={resource}
          onClose={() => setPanel(null)}
          onSaved={invalidate}
        />
      )}
      {resource && panel === "constraints" && (
        <EditConstraintRatingsDialog
          resource={resource}
          onClose={() => setPanel(null)}
          onSaved={invalidate}
        />
      )}
      {resource && panel === "disciplines" && (
        <EditDisciplinesDialog
          resource={resource}
          onClose={() => setPanel(null)}
          onSaved={invalidate}
        />
      )}
    </>
  );
}

function DisciplinesSummary({ resourceId }: { resourceId: string }) {
  const { data } = useQuery<DisciplineGrant[]>({
    queryKey: ["resource-disciplines", resourceId],
    queryFn: () => jsonFetch<DisciplineGrant[]>(`/api/resources/${resourceId}/disciplines`),
  });
  if (!data || data.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-border bg-card/50 px-3 py-2 text-[11px] text-muted-foreground">
        No active Disciplines.
      </div>
    );
  }
  return (
    <div className="flex flex-wrap gap-1">
      {data.map((g) => (
        <DisciplineChip key={g.grantId} code={g.code} title={g.name} />
      ))}
    </div>
  );
}
